import * as React from "react"
import { 
  Home, 
  FileText, 
  Plus, 
  User, 
  CreditCard, 
  HelpCircle, 
  Settings, 
  LogOut,
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  FolderOpen,
  Mail,
  Shield,
  Menu,
  X
} from "lucide-react"
import { cn } from "../../lib/utils"
import { AppStep } from "../../src/types"

interface NavItem {
  label: string
  icon: React.ElementType
  step: AppStep
  badge?: string | number
}

interface SidebarProps {
  currentStep: AppStep
  onNavigate: (step: AppStep) => void
  isAdmin: boolean
  isCollapsed: boolean
  onToggleCollapse: () => void
  isMobileOpen: boolean
  onMobileClose: () => void
  creditBalance?: number
  userEmail?: string
  onSignOut: () => void
}

const mainNavItems: NavItem[] = [
  { label: "Dashboard", icon: LayoutDashboard, step: AppStep.CHOOSE_ASSESSMENT },
  { label: "New Assessment", icon: Plus, step: AppStep.CREATE_ASSESSMENT },
]

const accountNavItems: NavItem[] = [
  { label: "My Account", icon: User, step: AppStep.ACCOUNT },
  { label: "Help & Support", icon: HelpCircle, step: AppStep.HELP_CONTACT },
  { label: "Contact Us", icon: Mail, step: AppStep.CONTACT_US },
]

const infoNavItems: NavItem[] = [
  { label: "About", icon: FileText, step: AppStep.ABOUT },
]

export function Sidebar({
  currentStep,
  onNavigate,
  isAdmin,
  isCollapsed,
  onToggleCollapse,
  isMobileOpen,
  onMobileClose,
  creditBalance,
  userEmail,
  onSignOut
}: SidebarProps) {
  const isActive = (step: AppStep) => currentStep === step

  const NavButton = ({ item, collapsed = false }: { item: NavItem; collapsed?: boolean }) => {
    const Icon = item.icon
    const active = isActive(item.step)
    
    return (
      <button
        onClick={() => {
          onNavigate(item.step)
          onMobileClose()
        }}
        className={cn(
          "flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors",
          active 
            ? "bg-primary text-primary-foreground" 
            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
          collapsed && "justify-center px-2"
        )}
        title={collapsed ? item.label : undefined}
      >
        <Icon className="h-5 w-5 shrink-0" />
        {!collapsed && (
          <>
            <span className="flex-1 text-left">{item.label}</span>
            {item.badge && (
              <span className="ml-auto bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">
                {item.badge}
              </span>
            )}
          </>
        )}
      </button>
    )
  }

  const SidebarContent = ({ collapsed = false }: { collapsed?: boolean }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={cn(
        "flex items-center h-16 px-4 border-b border-border shrink-0",
        collapsed ? "justify-center" : "gap-3"
      )}>
        <img
          src="https://firebasestorage.googleapis.com/v0/b/history-essay-bot.firebasestorage.app/o/transparent%20logo%20market%20mate%20(2).webp?alt=media&token=577c40fb-e028-4260-b65d-3545512569dd"
          alt="AI Marker Mate"
          className="h-8 w-8 object-contain shrink-0"
        />
        {!collapsed && (
          <span className="font-bold text-lg text-foreground truncate">AI Marker Mate</span>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
        {/* Main Navigation */}
        <div className="space-y-1">
          {!collapsed && (
            <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Main
            </p>
          )}
          {mainNavItems.map((item) => (
            <NavButton key={item.step} item={item} collapsed={collapsed} />
          ))}
        </div>

        {/* Account Navigation */}
        <div className="space-y-1">
          {!collapsed && (
            <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Account
            </p>
          )}
          {accountNavItems.map((item) => (
            <NavButton key={item.step} item={item} collapsed={collapsed} />
          ))}
        </div>

        {/* Info Navigation */}
        <div className="space-y-1">
          {!collapsed && (
            <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Info
            </p>
          )}
          {infoNavItems.map((item) => (
            <NavButton key={item.step} item={item} collapsed={collapsed} />
          ))}
        </div>

        {/* Admin */}
        {isAdmin && (
          <div className="space-y-1">
            {!collapsed && (
              <p className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                Admin
              </p>
            )}
            <NavButton 
              item={{ label: "Admin Dashboard", icon: Shield, step: AppStep.ADMIN_DASHBOARD }} 
              collapsed={collapsed} 
            />
          </div>
        )}
      </div>

      {/* User Section */}
      <div className={cn(
        "border-t border-border p-3 shrink-0",
        collapsed && "px-2"
      )}>
        {/* Credit Balance */}
        {creditBalance !== undefined && !collapsed && (
          <div className="mb-3 px-3 py-2 bg-accent rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Credits</span>
              <span className="font-semibold text-foreground">{creditBalance}</span>
            </div>
          </div>
        )}
        
        {/* User Info & Sign Out */}
        <div className={cn(
          "flex items-center gap-3",
          collapsed && "flex-col"
        )}>
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <User className="h-4 w-4 text-primary" />
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {userEmail || "User"}
              </p>
            </div>
          )}
          <button
            onClick={onSignOut}
            className={cn(
              "p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors",
              collapsed && "mt-2"
            )}
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Collapse Toggle (Desktop only) */}
      <button
        onClick={onToggleCollapse}
        className="hidden lg:flex items-center justify-center h-10 border-t border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
      >
        {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>
    </div>
  )

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 ease-in-out lg:hidden",
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="absolute top-4 right-4">
          <button
            onClick={onMobileClose}
            className="p-2 text-muted-foreground hover:text-foreground rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <SidebarContent collapsed={false} />
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 bg-card border-r border-border transition-all duration-200",
          isCollapsed ? "lg:w-16" : "lg:w-64"
        )}
      >
        <SidebarContent collapsed={isCollapsed} />
      </aside>
    </>
  )
}

export function SidebarTrigger({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="lg:hidden p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
    >
      <Menu className="h-5 w-5" />
    </button>
  )
}
